import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BotConfiguration = ({ botConfig }) => {
  const [activeTab, setActiveTab] = useState('strategy');

  const tabs = [
    { id: 'strategy', label: 'Strategy', icon: 'Target' },
    { id: 'risk', label: 'Risk Management', icon: 'Shield' },
    { id: 'pairs', label: 'Trading Pairs', icon: 'TrendingUp' },
    { id: 'schedule', label: 'Schedule', icon: 'Clock' }
  ];

  const ConfigSection = ({ title, children, tooltip }) => (
    <div className="mb-6">
      <div className="flex items-center space-x-2 mb-3">
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        {tooltip && (
          <div className="group relative">
            <Icon name="Info" size={14} className="text-muted-foreground cursor-help" />
            <div className="absolute left-0 md:left-auto md:right-0 top-6 w-64 p-3 bg-popover border border-border rounded-lg text-sm opacity-0 group-hover:opacity-100 pointer-events-none trading-transition z-50">
              {tooltip}
            </div>
          </div>
        )}
      </div>
      {children}
    </div>
  );

  const ConfigItem = ({ label, value, type = 'text' }) => (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 gap-1">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span
        className={`text-sm font-medium break-words ${
          type === 'success'
            ? 'text-success'
            : type === 'error'
            ? 'text-error'
            : type === 'warning'
            ? 'text-warning'
            : 'text-foreground'
        }`}
      >
        {value}
      </span>
    </div>
  );

  /** Strategy Tab */
  const renderStrategyTab = () => (
    <div className="space-y-6">
      <ConfigSection title="Strategy Details" tooltip="Core trading strategy parameters and logic">
        <div className="space-y-2">
          <ConfigItem label="Strategy Type" value={botConfig?.strategyType} />
          <ConfigItem label="Signal Source" value={botConfig?.signalSource} />
          <ConfigItem label="Configuration" value={botConfig?.entryConditions} />
          <ConfigItem label="Exit Conditions" value={botConfig?.exitConditions} />
        </div>
      </ConfigSection>

      <ConfigSection title="Technical Indicators" tooltip="Technical analysis indicators used for trade signals">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {botConfig?.indicators?.map((indicator, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">{indicator?.name}</span>
                <span className="text-xs text-muted-foreground">{indicator?.period}</span>
              </div>
              <div className="text-xs text-muted-foreground">{indicator?.parameters}</div>
            </div>
          ))}
        </div>
      </ConfigSection>
    </div>
  );

  /** Risk Tab */
  const renderRiskTab = () => (
    <div className="space-y-6">
      <ConfigSection title="Position Sizing" tooltip="How the bot calculates position sizes for each trade">
        <div className="space-y-2">
          <ConfigItem label="Position Size Method" value={botConfig?.positionSizeMethod} />
          <ConfigItem label="Risk Per Trade" value={botConfig?.riskPerTrade} type="warning" />
          <ConfigItem label="Max Position Size" value={botConfig?.maxPositionSize} />
          <ConfigItem label="Min Position Size" value={botConfig?.minPositionSize} />
        </div>
      </ConfigSection>

      <ConfigSection title="Risk Limits" tooltip="Safety mechanisms to protect your capital">
        <div className="space-y-2">
          <ConfigItem label="Stop Loss" value={botConfig?.stopLoss} type="error" />
          <ConfigItem label="Take Profit" value={botConfig?.takeProfit} type="success" />
          <ConfigItem label="Max Daily Loss" value={botConfig?.maxDailyLoss} type="error" />
          <ConfigItem label="Max Drawdown" value={botConfig?.maxDrawdown} type="warning" />
        </div>
      </ConfigSection>
    </div>
  );

  /** Pairs Tab */
  const renderPairsTab = () => (
    <div className="space-y-6">
      <ConfigSection title="Active Trading Pairs" tooltip="Cryptocurrency pairs this bot is configured to trade">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {botConfig?.tradingPairs?.map((pair, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-primary">{pair?.base}</span>
                  </div>
                  <span className="text-sm font-medium text-foreground">{pair?.symbol}</span>
                </div>
                <div
                  className={`w-2 h-2 rounded-full ${
                    pair?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'
                  }`}
                />
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                Min: ${pair?.minNotional} • Max: ${pair?.maxNotional}
              </div>
            </div>
          ))}
        </div>
      </ConfigSection>

      <ConfigSection title="Exchange Settings" tooltip="Exchange-specific configuration and API settings">
        <div className="space-y-2">
          <ConfigItem label="Primary Exchange" value={botConfig?.primaryExchange} />
          <ConfigItem label="API Connection" value="Connected" type="success" />
          <ConfigItem label="Trading Mode" value={botConfig?.tradingMode} />
          <ConfigItem label="Order Type" value={botConfig?.orderType} />
        </div>
      </ConfigSection>
    </div>
  );

  /** Schedule Tab */
  const renderScheduleTab = () => (
    <div className="space-y-6">
      <ConfigSection title="Trading Schedule" tooltip="When the bot is active and monitoring markets">
        <div className="space-y-2">
          <ConfigItem label="Trading Hours" value={botConfig?.tradingHours} />
          <ConfigItem label="Timezone" value={botConfig?.timezone} />
          <ConfigItem label="Weekend Trading" value={botConfig?.weekendTrading ? 'Enabled' : 'Disabled'} />
          <ConfigItem label="Holiday Trading" value={botConfig?.holidayTrading ? 'Enabled' : 'Disabled'} />
        </div>
      </ConfigSection>

      <ConfigSection title="Execution Frequency" tooltip="How often the bot checks for trading opportunities">
        <div className="space-y-2">
          <ConfigItem label="Signal Check Interval" value={botConfig?.signalInterval} />
          <ConfigItem label="Order Update Frequency" value={botConfig?.orderUpdateFreq} />
          <ConfigItem label="Portfolio Rebalance" value={botConfig?.rebalanceFreq} />
        </div>
      </ConfigSection>

      <ConfigSection title="Market Conditions" tooltip="Bot behavior during different market conditions">
        <div className="space-y-2">
          <ConfigItem label="High Volatility Mode" value={botConfig?.highVolMode} />
          <ConfigItem label="Low Liquidity Handling" value={botConfig?.lowLiquidityMode} />
          <ConfigItem label="News Event Pause" value={botConfig?.newsEventPause ? 'Enabled' : 'Disabled'} />
        </div>
      </ConfigSection>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'strategy':
        return renderStrategyTab();
      case 'risk':
        return renderRiskTab();
      case 'pairs':
        return renderPairsTab();
      case 'schedule':
        return renderScheduleTab();
      default:
        return renderStrategyTab();
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-foreground mb-1">Bot Configuration</h2>
          <p className="text-sm text-muted-foreground">Detailed settings and parameters</p>
        </div>
        <Button variant="ghost" size="sm" iconName="Copy" className="w-full sm:w-auto">
          Copy Configuration
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-6 bg-muted rounded-lg p-1">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center justify-center sm:justify-start gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-md trading-transition flex-1 sm:flex-none ${
              activeTab === tab?.id
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span className="hidden sm:inline">{tab?.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-80 sm:min-h-96">{renderTabContent()}</div>
    </div>
  );
};

export default BotConfiguration;
